/**
 * Endpoints API V2 — wrappers types par-dessus apiClient.
 *
 * Pattern central : chaque domaine expose un objet figé { list, get, create, ... }
 * qui s'aligne sur les actions backend `domaine.verbe`.
 *
 * Avantages :
 *   - 1 seul endroit ou changer le nom d'une action si l'API bouge
 *   - Typage du retour (envelope { ok, ... } depacquetee automatiquement)
 *   - Pas de magic string `'/chantiers.list'` parpille dans les composants
 *
 * Convention de retour : l'API renvoie toujours { ok: true, ...payload }. Les
 * helpers ci-dessous renvoient le `payload` (l'envelope est validee une fois
 * dans `unwrap`).
 */

import apiClient from './client';
import type {
  Chantier,
  ChantierAssignment,
  ChantierBudgetConsommation,
  ChantierCreateInput,
  ChantierUpdateInput,
  FicheHeures,
  FicheHeuresLigne,
  FicheHeuresLigneInput,
  FicheHeuresStatus,
  JournalCreateInput,
  JournalEntry,
  JournalUpdateInput,
  Livraison,
  LivraisonCreateInput,
  LivraisonItem,
  LivraisonReceptionInput,
  LivraisonStatus,
  LivraisonUpdateInput,
  Pointage,
  PointageCreateInput,
  QrPurpose,
  QrPurposeOrAuto,
  QrToken,
  TenantPaieSettings,
} from '../types/models';

interface ApiEnvelope {
  ok: boolean;
  error?: string;
  message?: string;
  [k: string]: unknown;
}

async function unwrap<T extends Record<string, unknown>>(
  res: { data: ApiEnvelope & T },
): Promise<T> {
  if (!res.data || res.data.ok === false) {
    const code = res.data?.error ?? 'unknown_error';
    const msg = res.data?.message ?? 'Erreur API';
    throw new ApiError(code, msg);
  }
  return res.data as T;
}

export class ApiError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

// =============================================================================
// Domaine : auth
// =============================================================================
export const authApi = {
  login: (email: string, password: string) =>
    apiClient.post('/auth.login_v2', { email, password }).then((r) => r.data),
  me: () => apiClient.get('/auth.me').then((r) => r.data),
};

// =============================================================================
// Domaine : tenant (branding, theme)
// =============================================================================
export const tenantApi = {
  theme: () => apiClient.get('/tenant.theme').then((r) => r.data),
  updateBranding: (patch: Record<string, unknown>) =>
    apiClient.post('/tenant.update_branding', patch).then((r) => r.data),
};

// =============================================================================
// Domaine : chantiers (CP-1)
// =============================================================================
export const chantiersApi = {
  list: async (): Promise<{ chantiers: Chantier[] }> =>
    unwrap(await apiClient.get('/chantiers.list')),

  get: async (id: number): Promise<{ chantier: Chantier }> =>
    unwrap(await apiClient.get('/chantiers.get', { params: { id } })),

  create: async (input: ChantierCreateInput): Promise<{ id: number }> =>
    unwrap(await apiClient.post('/chantiers.create', input)),

  update: async (id: number, patch: ChantierUpdateInput): Promise<{ updated: true }> =>
    unwrap(await apiClient.post('/chantiers.update', { id, ...patch })),

  archive: async (id: number): Promise<{ archived: true }> =>
    unwrap(await apiClient.post('/chantiers.archive', { id })),

  /** Stats budget materiaux + heures cumulees (management uniquement). */
  budgetConsommation: async (chantier_id: number): Promise<ChantierBudgetConsommation> =>
    unwrap(await apiClient.get('/chantiers.budget_consommation', { params: { chantier_id } })) as unknown as ChantierBudgetConsommation,
};

// =============================================================================
// Domaine : KPI consolides du tenant (dashboard home)
// =============================================================================
export interface TenantOverview {
  /** Marge moyenne PREVISIONNELLE (devis HT - budgets prevus) / devis HT × 100.
   *  Null si aucun chantier actif n'a de montant_devis_ht > 0. */
  marge_prevue_pct: number | null;
  /** Marge moyenne CONSOMMEE en temps reel.
   *  Couts = materiaux reels (livraisons recues) + heures validees × taux horaire
   *  + budget sous-traitance (engagement contractuel).
   *  Null si aucun chantier actif n'a de montant_devis_ht > 0. */
  marge_consommee_pct: number | null;
  /** Nb de chantiers actifs avec un montant_devis_ht renseigne. */
  chantiers_count: number;
  revenus_total: number;
  couts_prevus_total: number;
  couts_reels_total: number;
  couts_materiaux_reels: number;
  couts_main_oeuvre_reels: number;
  couts_sous_traitance: number;
  heures_ponderees_total: number;
  /** CHF/h applique au calcul main d'oeuvre (depuis tenant_paie_settings). */
  taux_horaire_applique: number;
}

export const kpiApi = {
  tenantOverview: async (): Promise<TenantOverview> =>
    unwrap(await apiClient.get('/kpi.tenant_overview')) as unknown as TenantOverview,
};

// =============================================================================
// Domaine : chantier_assignments (CP-1)
// =============================================================================
export const assignmentsApi = {
  list: async (chantier_id: number): Promise<{ assignments: ChantierAssignment[] }> =>
    unwrap(await apiClient.get('/chantier_assignments.list', { params: { chantier_id } })),

  assign: async (
    chantier_id: number,
    user_id: number,
    role: ChantierAssignment['role'],
  ): Promise<{ id: number }> =>
    unwrap(await apiClient.post('/chantier_assignments.assign', { chantier_id, user_id, role })),

  unassign: async (id: number): Promise<{ unassigned: true }> =>
    unwrap(await apiClient.post('/chantier_assignments.unassign', { id })),
};

// =============================================================================
// Domaine : users (utilise pour selecteurs chef/conducteur/equipe)
// =============================================================================
export const usersApi = {
  list: async (role?: string): Promise<{ users: import('../types/models').User[] }> =>
    unwrap(await apiClient.get('/users.list', { params: role ? { role } : {} })),

  updateLocale: async (locale: string): Promise<{ preferred_locale: string }> =>
    unwrap(await apiClient.post('/users.update_locale', { locale })),
};

// =============================================================================
// Domaine : pointages (CP-2)
// =============================================================================
export interface PointagesListFilters {
  chantier_id?: number;
  compagnon_id?: number;
  date_from?: string;
  date_to?: string;
  anomaly_only?: boolean;
}

export const pointagesApi = {
  list: async (filters: PointagesListFilters = {}): Promise<{ pointages: Pointage[] }> => {
    const params: Record<string, string | number> = {};
    if (filters.chantier_id)  params.chantier_id  = filters.chantier_id;
    if (filters.compagnon_id) params.compagnon_id = filters.compagnon_id;
    if (filters.date_from)    params.date_from    = filters.date_from;
    if (filters.date_to)      params.date_to      = filters.date_to;
    if (filters.anomaly_only) params.anomaly_only = 1;
    return unwrap(await apiClient.get('/pointages.list', { params }));
  },

  get: async (id: number): Promise<{ pointage: Pointage }> =>
    unwrap(await apiClient.get('/pointages.get', { params: { id } })),

  create: async (input: PointageCreateInput): Promise<{ id: number }> =>
    unwrap(await apiClient.post('/pointages.create', input)),

  update: async (id: number, patch: Partial<PointageCreateInput>): Promise<{ updated: true }> =>
    unwrap(await apiClient.post('/pointages.update', { id, ...patch })),

  qrScan: async (
    payload: string,
    signature: string,
    geo?: { latitude: number; longitude: number; accuracy?: number },
  ): Promise<{ pointage: Pointage; scanned_action: QrPurpose }> =>
    unwrap(await apiClient.post('/pointages.qr_scan', {
      payload, signature,
      ...(geo ? { latitude: geo.latitude, longitude: geo.longitude, accuracy: geo.accuracy } : {}),
    })),

  /**
   * Auto-pointage manuel (chef/conducteur). Pas de QR : l'heure est
   * celle du serveur (Europe/Zurich), source='manual'.
   * Backend refuse si le creneau est deja rempli (pour eviter d'ecraser).
   */
  selfAction: async (
    chantier_id: number,
    action: QrPurpose,
  ): Promise<{ id: number; action: QrPurpose; time: string }> =>
    unwrap(await apiClient.post('/pointages.self_action', { chantier_id, action })),
};

// =============================================================================
// Domaine : qr_tokens (CP-2)
// =============================================================================
export const qrTokensApi = {
  /**
   * Genere un QR pour un chantier. `purpose` est optionnel — par defaut 'auto'
   * (le serveur calcule l'action au scan selon l'etat du compagnon).
   */
  generate: async (chantier_id: number, purpose: QrPurposeOrAuto = 'auto'): Promise<QrToken> => {
    const res = await unwrap<Record<string, unknown>>(
      await apiClient.post('/qr_tokens.generate', { chantier_id, purpose }),
    );
    return res as unknown as QrToken;
  },
};

// =============================================================================
// Domaine : journal (CP-3)
// =============================================================================
export interface JournalListFilters {
  chantier_id: number;
  limit?: number;
  before_id?: number;
}

/**
 * Convertit un payload {champs + photo_data base64} en FormData multipart.
 *
 * Pourquoi multipart : sur Infomaniak mutualise, mod_security/FastCGI vide
 * php://input pour les payloads JSON volumineux (> 16KB observe). Le format
 * multipart contourne ce filtre.
 *
 * Pourquoi photo_b64 en CHAMP TEXTE (pas en file Blob) : Infomaniak n'a pas
 * configure upload_tmp_dir pour ce site, donc PHP refuse les uploads de
 * fichiers (UPLOAD_ERR_NO_TMP_DIR = 6). En envoyant la base64 dans un champ
 * texte, on evite move_uploaded_file et tout le mecanisme de tmp dir.
 * Le backend lit directement $_POST['photo_b64'] et base64_decode.
 */
function buildJournalFormData(payload: Record<string, unknown>): FormData {
  const fd = new FormData();
  for (const [key, value] of Object.entries(payload)) {
    if (key === 'photo_data') continue; // converti en photo_b64 en bas
    if (value === null || value === undefined) continue;
    fd.append(key, String(value));
  }
  const photoData = payload.photo_data;
  if (typeof photoData === 'string' && photoData.length > 0) {
    // On envoie la data URL/base64 telle quelle dans un champ texte.
    // Backend supporte les 2 formats (avec ou sans prefixe data:...).
    fd.append('photo_b64', photoData);
  }
  return fd;
}

export const journalApi = {
  list: async (filters: JournalListFilters): Promise<{ entries: JournalEntry[]; count: number }> =>
    unwrap(await apiClient.get('/journal.list', { params: filters })),

  get: async (id: number): Promise<{ entry: JournalEntry }> =>
    unwrap(await apiClient.get('/journal.get', { params: { id } })),

  create: async (input: JournalCreateInput): Promise<{ id: number }> => {
    // Si pas de photo : JSON classique (rapide, leger)
    // Si photo : multipart/form-data (contourne le filtrage Infomaniak)
    if (!input.photo_data) {
      return unwrap(await apiClient.post('/journal.create', input));
    }
    const fd = buildJournalFormData(input as unknown as Record<string, unknown>);
    // Important : ne PAS forcer Content-Type. Axios detecte FormData et set
    // automatiquement 'multipart/form-data; boundary=...' avec la bonne
    // boundary calculee. Forcer le header ecraserait la boundary -> le
    // serveur ne pourrait pas parser et $_FILES serait vide.
    return unwrap(await apiClient.post('/journal.create', fd, {
      headers: { 'Content-Type': undefined as unknown as string },
    }));
  },

  update: async (id: number, patch: JournalUpdateInput): Promise<{ updated: true }> => {
    const payload = { id, ...patch };
    if (!patch.photo_data) {
      return unwrap(await apiClient.post('/journal.update', payload));
    }
    const fd = buildJournalFormData(payload as unknown as Record<string, unknown>);
    return unwrap(await apiClient.post('/journal.update', fd, {
      headers: { 'Content-Type': undefined as unknown as string },
    }));
  },

  delete: async (id: number): Promise<{ deleted: true }> =>
    unwrap(await apiClient.post('/journal.delete', { id })),
};

// =============================================================================
// Domaine : fiches_heures (CP-5)
// =============================================================================
export interface FichesHeuresListFilters {
  week_start?: string;
  status?: FicheHeuresStatus;
  chantier_id?: number;
  compagnon_id?: number;
  anomaly_only?: boolean;
}

export const fichesHeuresApi = {
  list: async (filters: FichesHeuresListFilters = {}): Promise<{ fiches: FicheHeures[] }> => {
    const params: Record<string, string | number> = {};
    if (filters.week_start) params.week_start = filters.week_start;
    if (filters.status) params.status = filters.status;
    if (filters.chantier_id) params.chantier_id = filters.chantier_id;
    if (filters.compagnon_id) params.compagnon_id = filters.compagnon_id;
    if (filters.anomaly_only) params.anomaly_only = 1;
    return unwrap(await apiClient.get('/fiches_heures.list', { params }));
  },

  get: async (id: number): Promise<{ fiche: FicheHeures; lignes: FicheHeuresLigne[] }> =>
    unwrap(await apiClient.get('/fiches_heures.get', { params: { id } })),

  generate: async (week_start?: string): Promise<{ week_start: string; created: number; updated: number }> =>
    unwrap(await apiClient.post('/fiches_heures.generate', week_start ? { week_start } : {})),

  update: async (
    id: number,
    patch: { site_location?: string | null; site_manager_name?: string | null; notes_internes?: string | null; lignes?: FicheHeuresLigneInput[] },
  ): Promise<{ updated: true }> =>
    unwrap(await apiClient.post('/fiches_heures.update', { id, ...patch })),

  submit: async (id: number): Promise<{ status: 'submitted' }> =>
    unwrap(await apiClient.post('/fiches_heures.submit', { id })),

  validateChef: async (id: number): Promise<{ status: 'validated_chef' }> =>
    unwrap(await apiClient.post('/fiches_heures.validate_chef', { id })),

  validateDirection: async (id: number): Promise<{ status: 'validated_direction' }> =>
    unwrap(await apiClient.post('/fiches_heures.validate_direction', { id })),

  reject: async (id: number, rejection_reason: string): Promise<{ status: 'draft'; rejection_reason: string }> =>
    unwrap(await apiClient.post('/fiches_heures.reject', { id, rejection_reason })),

  /**
   * Le compagnon demande une correction sur sa fiche deja soumise.
   * La fiche revient en 'draft' avec un motif (prefixe '[Demande compagnon]').
   */
  requestCorrection: async (id: number, reason: string): Promise<{ status: 'draft' }> =>
    unwrap(await apiClient.post('/fiches_heures.request_correction', { id, reason })),

  /**
   * Telecharge l'export Excel d'une semaine. Le backend repond avec un
   * stream binaire xlsx (pas un JSON), donc on bypass `unwrap`.
   */
  exportExcel: async (week_start: string, status: FicheHeuresStatus = 'validated_direction', chantier_id?: number): Promise<Blob> => {
    const params: Record<string, string | number> = { week_start, status };
    if (chantier_id) params.chantier_id = chantier_id;
    const res = await apiClient.get('/fiches_heures.export_excel', { params, responseType: 'blob' });
    return res.data as Blob;
  },
};

// =============================================================================
// Domaine : tenant.paie_settings (CP-5)
// =============================================================================
export const paieSettingsApi = {
  get: async (): Promise<{ paie_settings: TenantPaieSettings }> =>
    unwrap(await apiClient.get('/tenant.paie_settings_get')),

  update: async (patch: Partial<TenantPaieSettings>): Promise<{ updated: true }> =>
    unwrap(await apiClient.post('/tenant.paie_settings_update', patch)),
};

// =============================================================================
// Domaine : livraisons (CP-4)
// =============================================================================
export interface LivraisonsListFilters {
  chantier_id?: number;
  status?: LivraisonStatus;
  date_from?: string;
  date_to?: string;
}

export const livraisonsApi = {
  list: async (filters: LivraisonsListFilters = {}): Promise<{ livraisons: Livraison[] }> => {
    const params: Record<string, string | number> = {};
    if (filters.chantier_id) params.chantier_id = filters.chantier_id;
    if (filters.status)      params.status      = filters.status;
    if (filters.date_from)   params.date_from   = filters.date_from;
    if (filters.date_to)     params.date_to     = filters.date_to;
    return unwrap(await apiClient.get('/livraisons.list', { params }));
  },

  get: async (id: number): Promise<{ livraison: Livraison; items: LivraisonItem[] }> =>
    unwrap(await apiClient.get('/livraisons.get', { params: { id } })),

  create: async (input: LivraisonCreateInput): Promise<{ id: number }> =>
    unwrap(await apiClient.post('/livraisons.create', input)),

  update: async (id: number, patch: LivraisonUpdateInput): Promise<{ updated: true }> =>
    unwrap(await apiClient.post('/livraisons.update', { id, ...patch })),

  /**
   * Reception : photo bon de livraison + 2 signatures obligatoires.
   * Comme journal/CP-3, les photos voyagent en multipart pour contourner
   * le filtre Infomaniak sur les payloads JSON > 16KB.
   */
  confirmReception: async (id: number, input: LivraisonReceptionInput): Promise<{ status: 'received' }> => {
    // Conversion en FormData : photo + 2 signatures sont en base64 mais
    // envoyees en champs texte (pattern photo_b64 du journal).
    const fd = new FormData();
    fd.append('id', String(id));
    fd.append('receipt_photo_data', input.receipt_photo_data);
    fd.append('signature_receveur_data', input.signature_receveur_data);
    fd.append('signature_livreur_data', input.signature_livreur_data);
    if (input.items_received) {
      fd.append('items_received', JSON.stringify(input.items_received));
    }
    return unwrap(await apiClient.post('/livraisons.confirm_reception', fd, {
      headers: { 'Content-Type': undefined as unknown as string },
    }));
  },

  dispute: async (
    id: number,
    dispute_reason: string,
    dispute_photo_data?: string,
  ): Promise<{ status: 'disputed' }> => {
    if (dispute_photo_data) {
      // Multipart si photo (meme raison que reception)
      const fd = new FormData();
      fd.append('id', String(id));
      fd.append('dispute_reason', dispute_reason);
      fd.append('dispute_photo_data', dispute_photo_data);
      return unwrap(await apiClient.post('/livraisons.dispute', fd, {
        headers: { 'Content-Type': undefined as unknown as string },
      }));
    }
    return unwrap(await apiClient.post('/livraisons.dispute', { id, dispute_reason }));
  },

  cancel: async (id: number, cancelled_reason: string): Promise<{ status: 'cancelled' }> =>
    unwrap(await apiClient.post('/livraisons.cancel', { id, cancelled_reason })),

  delete: async (id: number): Promise<{ deleted: true }> =>
    unwrap(await apiClient.post('/livraisons.delete', { id })),
};

// Note : `items_received` est envoye en JSON string dans le multipart car
// FormData ne sait pas serialiser des arrays. Backend fait json_decode.

// =============================================================================
// DEMANDES (CP-6) — conges / acomptes / frais
// =============================================================================
export interface DemandesListFilters {
  type?: import('../types/models').DemandeType | '';
  status?: import('../types/models').DemandeStatus | '';
}

export const demandesApi = {
  list: async (filters: DemandesListFilters = {}): Promise<{ demandes: import('../types/models').Demande[] }> => {
    const params: Record<string, string> = {};
    if (filters.type)   params.type   = filters.type;
    if (filters.status) params.status = filters.status;
    return unwrap(await apiClient.get('/demandes.list', { params }));
  },

  get: async (id: number): Promise<{ demande: import('../types/models').Demande }> =>
    unwrap(await apiClient.get('/demandes.get', { params: { id } })),

  /**
   * Recupere le document image attache a une demande.
   * Le backend renvoie 403 si l'appelant n'est ni l'auteur ni management
   * (ni conducteur sur type=frais). Defense en profondeur cote serveur.
   */
  getDocument: async (id: number): Promise<import('../types/models').DemandeDocument> =>
    unwrap(await apiClient.get('/demandes.get_document', { params: { id } })) as unknown as import('../types/models').DemandeDocument,

  /**
   * Cree une demande. Si `document_b64` est fourni, on bascule en multipart
   * (pattern photo_b64 du journal CP-3 : evite mod_security Infomaniak
   * sur payloads JSON > 16KB et UPLOAD_ERR_NO_TMP_DIR).
   */
  create: async (input: import('../types/models').DemandeCreateInput): Promise<{ id: number; has_document?: boolean }> => {
    if (input.document_b64) {
      const fd = new FormData();
      fd.append('type', input.type);
      if (input.start_date) fd.append('start_date', input.start_date);
      if (input.end_date)   fd.append('end_date', input.end_date);
      if (input.reason)     fd.append('reason', input.reason);
      if (input.montant !== undefined && input.montant !== null) fd.append('montant', String(input.montant));
      fd.append('document_b64', input.document_b64);
      if (input.document_filename) fd.append('document_filename', input.document_filename);
      return unwrap(await apiClient.post('/demandes.create', fd, {
        headers: { 'Content-Type': undefined as unknown as string },
      }));
    }
    return unwrap(await apiClient.post('/demandes.create', input));
  },

  update: async (id: number, patch: import('../types/models').DemandeUpdateInput): Promise<{ updated: true }> => {
    if (patch.document_b64 || patch.remove_document) {
      const fd = new FormData();
      fd.append('id', String(id));
      if (patch.start_date !== undefined) fd.append('start_date', patch.start_date ?? '');
      if (patch.end_date !== undefined)   fd.append('end_date', patch.end_date ?? '');
      if (patch.reason !== undefined)     fd.append('reason', patch.reason ?? '');
      if (patch.montant !== undefined && patch.montant !== null) fd.append('montant', String(patch.montant));
      if (patch.document_b64) {
        fd.append('document_b64', patch.document_b64);
        if (patch.document_filename) fd.append('document_filename', patch.document_filename);
      }
      if (patch.remove_document) fd.append('remove_document', '1');
      return unwrap(await apiClient.post('/demandes.update', fd, {
        headers: { 'Content-Type': undefined as unknown as string },
      }));
    }
    return unwrap(await apiClient.post('/demandes.update', { id, ...patch }));
  },

  cancel: async (id: number): Promise<{ status: 'cancelled' }> =>
    unwrap(await apiClient.post('/demandes.cancel', { id })),

  approveChef: async (id: number): Promise<{ status: 'approved_chef' }> =>
    unwrap(await apiClient.post('/demandes.approve_chef', { id })),

  approveDirection: async (id: number): Promise<{ status: 'approved_direction' }> =>
    unwrap(await apiClient.post('/demandes.approve_direction', { id })),

  refuse: async (id: number, refusal_reason: string): Promise<{ status: 'refused' }> =>
    unwrap(await apiClient.post('/demandes.refuse', { id, refusal_reason })),

  delete: async (id: number): Promise<{ deleted: true }> =>
    unwrap(await apiClient.post('/demandes.delete', { id })),
};

// =============================================================================
// VEHICULES (Palier 1) — parc auto
// =============================================================================
export interface VehiculesListFilters {
  statut?: import('../types/models').VehiculeStatut | '';
}

export const vehiculesApi = {
  list: async (filters: VehiculesListFilters = {}): Promise<{ vehicules: import('../types/models').Vehicule[] }> => {
    const params: Record<string, string> = {};
    if (filters.statut) params.statut = filters.statut;
    return unwrap(await apiClient.get('/vehicules.list', { params }));
  },

  get: async (id: number): Promise<{
    vehicule: import('../types/models').Vehicule;
    utilisation_en_cours: {
      id: number;
      user_id: number;
      user_name: string | null;
      chantier_id: number | null;
      chantier_title: string | null;
      date_debut: string;
      km_debut: number;
      usage_type: import('../types/models').UtilisationUsageType;
      motif: string | null;
    } | null;
  }> =>
    unwrap(await apiClient.get('/vehicules.get', { params: { id } })),

  create: async (input: import('../types/models').VehiculeCreateInput): Promise<{ id: number }> => {
    if (input.photo_b64) {
      const fd = new FormData();
      for (const [k, v] of Object.entries(input)) {
        if (v === undefined || v === null) continue;
        if (k === 'photo_b64') fd.append('photo_b64', String(v));
        else fd.append(k, String(v));
      }
      return unwrap(await apiClient.post('/vehicules.create', fd, {
        headers: { 'Content-Type': undefined as unknown as string },
      }));
    }
    return unwrap(await apiClient.post('/vehicules.create', input));
  },

  update: async (id: number, patch: import('../types/models').VehiculeUpdateInput): Promise<{ updated: true }> => {
    if (patch.photo_b64 || patch.remove_photo) {
      const fd = new FormData();
      fd.append('id', String(id));
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === null) continue;
        if (k === 'photo_b64') fd.append('photo_b64', String(v));
        else if (k === 'remove_photo' && v) fd.append('remove_photo', '1');
        else fd.append(k, String(v));
      }
      return unwrap(await apiClient.post('/vehicules.update', fd, {
        headers: { 'Content-Type': undefined as unknown as string },
      }));
    }
    return unwrap(await apiClient.post('/vehicules.update', { id, ...patch }));
  },

  delete: async (id: number): Promise<{ deleted: true }> =>
    unwrap(await apiClient.post('/vehicules.delete', { id })),

  alerts: async (): Promise<import('../types/models').VehiculesAlerts> =>
    unwrap(await apiClient.get('/vehicules.alerts')) as unknown as import('../types/models').VehiculesAlerts,
};

export interface UtilisationsListFilters {
  vehicule_id?: number;
  user_id?: number;
  active_only?: boolean;
}

export const vehiculeUtilisationsApi = {
  list: async (filters: UtilisationsListFilters = {}): Promise<{ utilisations: import('../types/models').VehiculeUtilisation[] }> => {
    const params: Record<string, string | number> = {};
    if (filters.vehicule_id) params.vehicule_id = filters.vehicule_id;
    if (filters.user_id)     params.user_id     = filters.user_id;
    if (filters.active_only) params.active_only = '1';
    return unwrap(await apiClient.get('/vehicule_utilisations.list', { params }));
  },

  get: async (id: number): Promise<{ utilisation: import('../types/models').VehiculeUtilisation }> =>
    unwrap(await apiClient.get('/vehicule_utilisations.get', { params: { id } })),

  start: async (input: import('../types/models').UtilisationStartInput): Promise<{ id: number }> =>
    unwrap(await apiClient.post('/vehicule_utilisations.start', input)),

  stop: async (input: import('../types/models').UtilisationStopInput): Promise<{ stopped: true; km_parcourus: number }> =>
    unwrap(await apiClient.post('/vehicule_utilisations.stop', input)),

  update: async (id: number, patch: import('../types/models').UtilisationUpdateInput): Promise<{ updated: true }> =>
    unwrap(await apiClient.post('/vehicule_utilisations.update', { id, ...patch })),

  delete: async (id: number): Promise<{ deleted: true }> =>
    unwrap(await apiClient.post('/vehicule_utilisations.delete', { id })),

  /** Page killer "Qui conduisait le véhicule X tel jour à telle heure ?" */
  findDriver: async (vehicule_id: number, at: string): Promise<import('../types/models').FindDriverResult> =>
    unwrap(await apiClient.get('/vehicule_utilisations.find_driver', { params: { vehicule_id, at } })) as unknown as import('../types/models').FindDriverResult,
};

// =============================================================================
// DEMO publique — auto-login sans mot de passe pour pitch commercial
// =============================================================================
export type DemoRole = 'admin' | 'direction' | 'conducteur' | 'chef' | 'compagnon';

export const demoApi = {
  /**
   * Auto-login sur compte demo whitelist. Pas de password requis.
   * Retourne JWT + user + tenant (slug=demo).
   */
  login: async (role: DemoRole): Promise<{
    token: string;
    user: { id: number; email: string; name: string; role: string; tenant_id: number };
    tenant: { id: number; name: string; slug: string };
    is_demo: true;
  }> =>
    unwrap(await apiClient.post('/auth.demo_login', { role })) as unknown as {
      token: string;
      user: { id: number; email: string; name: string; role: string; tenant_id: number };
      tenant: { id: number; name: string; slug: string };
      is_demo: true;
    },
};

// =============================================================================
// NOTIFICATIONS Phase 1 (in-app + polling)
// =============================================================================
export const notificationsApi = {
  list: async (unreadOnly = false, limit = 30): Promise<import('../types/models').NotificationsListResponse> => {
    const params: Record<string, string | number> = { limit };
    if (unreadOnly) params.unread_only = '1';
    return unwrap(await apiClient.get('/notifications.list', { params })) as unknown as import('../types/models').NotificationsListResponse;
  },

  markRead: async (id: number): Promise<{ updated: number }> =>
    unwrap(await apiClient.post('/notifications.mark_read', { id })),

  markAllRead: async (): Promise<{ marked: number }> =>
    unwrap(await apiClient.post('/notifications.mark_all_read', {})),

  getPreferences: async (): Promise<{ preferences: import('../types/models').NotificationPreferences }> =>
    unwrap(await apiClient.get('/notifications.preferences_get')),

  updatePreferences: async (prefs: Partial<import('../types/models').NotificationPreferences>): Promise<{ updated: number }> =>
    unwrap(await apiClient.post('/notifications.preferences_update', prefs)),
};
